{ Jadelet, Modal, Observable } from @danielx/dui

{ addMidiEventListener } from ../lib/midi/input.civet
{ beatOfMeasure } from ../lib/midi/measures.civet
{ readPracticeMetadata } from ../lib/midi/practice-metadata.civet
{ midiPitchToStaffNote } from ../lib/util.civet
{ type Card, buildPool, pieceBiasFromSong } from ../lib/lessons/cards.civet
{
  DAILY_GOAL_SECONDS
  addPracticeSeconds
  deckCounts
  loadDeck
  recordResults
  saveDeck
  sessionCards
  todaySeconds
} from ../lib/lessons/deck.civet
{ LessonSession } from ../lib/lessons/session.civet
{ lessonPlan, recordPass, targetRate, type PassStats } from ../lib/lessons/progress.civet
{ LESSONS_KEY } from ../const.civet

{floor, max, min, round} := Math

// "My Practice": a daily 15-minute lesson session of flash cards — note
// reading, chord and scale recognition — answered by playing them on the
// MIDI keyboard, scheduled by spaced repetition (lib/lessons/). Choosing a
// piece biases the cards toward its notes, key, and chords, and its
// practice-metadata sections get one-press A/B-loop drills.
Template := Jadelet.exec """
  section.my-practice
    h2 📚 My Practice
    button.close(click=@close) X
    section.home(class=@homeHidden)
      .goal
        .goal-bar
          .goal-fill(style=@goalStyle)
        p @goalText
      p.queue @queueText
      button.primary.start(click=@start) ▶ Start Practice
      h3 Piece
      p.piece-line @pieceText
      .piece-buttons
        button.flat(click=@usePiece) Use current song
        button.flat(click=@clearPiece class=@clearPieceHidden) ✕ Clear piece
      section.plan(class=@sectionsHidden)
        h3 Today's Plan
        @planItems
      section.sections(class=@sectionsHidden)
        h3 Sections
        @sectionItems
    section.card-session(class=@sessionHidden)
      .prompt
      p.instruction @promptLabel
      p.answer @answerText
      p.progress @progressText
      p.hint Play your MIDI keyboard to answer
      button.flat(click=@endSession) End Session
    section.summary(class=@summaryHidden)
      h3 Session Complete 🎉
      p @summaryText
      button.primary(click=@backHome) Done
"""

SectionRowTemplate := Jadelet.exec """
  button.flat(@click @title) @label
"""

// A small five-line staff with one note on it, for the note-reading cards.
// Positions follow midiPitchToStaffNote: middle C = 0, one diatonic step
// per unit; treble lines sit at 2..10, bass at -2..-10.
HALF := 7 // px per staff position
staffSvg := (n: number, key: number) ->
  [pos, accidental] := midiPitchToStaffNote n, key
  treble := pos >= 0
  base := if treble then 112 else 42
  y := (p: number) -> base - p * HALF
  linePositions := if treble then [2, 4, 6, 8, 10] else [-2, -4, -6, -8, -10]

  parts: string[] := []
  for p of linePositions
    parts.push `<line x1="16" y1="${y p}" x2="204" y2="${y p}" stroke="currentColor" stroke-width="1.5"/>`

  // clef, sized and centered against its staff
  if treble
    parts.push `<text x="24" y="${y(4) + 14}" font-size="58">\u{1d11e}</text>`
  else
    parts.push `<text x="24" y="${y(-6) + 8}" font-size="40">\u{1d122}</text>`

  // ledger lines from the staff edge out to the note
  ledgers: number[] := []
  [lo, hi] := if treble then [2, 10] else [-10, -2]
  p .= hi + 2
  while p <= pos
    ledgers.push p
    p += 2
  p = lo - 2
  while p >= pos
    ledgers.push p
    p -= 2
  for lp of ledgers
    parts.push `<line x1="118" y1="${y lp}" x2="150" y2="${y lp}" stroke="currentColor" stroke-width="1.5"/>`

  accidentalGlyph := switch accidental?.name
    "sharp" then "♯"
    "flat" then "♭"
    "natural" then "♮"
    else ""
  if accidentalGlyph
    parts.push `<text x="104" y="${y(pos) + 7}" font-size="24">${accidentalGlyph}</text>`

  parts.push `<ellipse cx="134" cy="${y pos}" rx="8.5" ry="6" transform="rotate(-14 134 ${y pos})" fill="currentColor"/>`

  `<svg viewBox="0 0 220 150" width="220" height="150" fill="currentColor">${parts.join ""}</svg>`

export MyPractice := (player: Player) ->
  deck := loadDeck localStorage, LESSONS_KEY
  save := -> saveDeck localStorage, LESSONS_KEY, deck

  screen := Observable "home"
  tick := Observable 0
  answerText := Observable ""
  elapsed := Observable 0 // seconds in the running session

  let session: ReturnType<typeof LessonSession>?
  let timerId: ReturnType<typeof setInterval>?
  sessionStart .= 0

  // Memoized per piece: queueText re-evaluates on every tick (each noteOn
  // during a session bumps it) and must not rebuild the whole card pool
  let poolCache: {piece: unknown, cards: Card[]}?
  pool := ->
    unless poolCache and poolCache.piece is deck.piece
      poolCache = {piece: deck.piece, cards: buildPool deck.piece}
    poolCache.cards

  // Section progress is keyed by the piece's identity so it survives edits
  pieceKey := ->
    readPracticeMetadata(player.song)?.sourceMd5 or player.song.title() or "untitled"

  pieceProgress := ->
    deck.progress ?= {}
    deck.progress[pieceKey()] ?= {}

  songSections := ->
    readPracticeMetadata(player.song)?.sections ?? []

  sectionBeats := (s: {startMeasure: number, endMeasure: number}) ->
    {timeSignatureMap} := player.song
    start := beatOfMeasure timeSignatureMap, s.startMeasure
    end := min beatOfMeasure(timeSignatureMap, s.endMeasure + 1), player.song.beatLength()
    [start, end] as [number, number]

  startDrill := (i: number, rate: number, wait: boolean) ->
    s := songSections()[i]
    return unless s
    [start, end] := sectionBeats s
    player.practiceRate max 0.5, min 1, rate
    player.waitForNote wait
    player.practiceLoop [start, end]
    player.currentBeat start
    player.enterMode "practice"
    Modal.hide()

  // Every completed judged pass whose loop matches a section feeds its
  // history — whether it started from a plan button or a hand-set loop.
  // Event bus, not a callback slot: other consumers can listen too.
  player.on "practicePassEnded", (info: {stats: PassStats, rate: number, loop: [number, number], complete: boolean}) ->
    sections := songSections()
    idx := sections.findIndex (s) ->
      [start, end] := sectionBeats s
      Math.abs(info.loop[0] - start) < 0.01 and Math.abs(info.loop[1] - end) < 0.01
    return if idx < 0
    recordPass pieceProgress(), idx, info, Date.now()
    save()
    tick.increment()

  hiddenUnless := (name: string) -> ->
    tick()
    "hidden" unless screen() is name

  promptEl := -> self.element.querySelector(".prompt") as HTMLElement

  renderPrompt := ->
    card := session?.current()
    el := promptEl()
    return unless el
    if card and card.kind is "note"
      el.innerHTML = staffSvg card.n!, deck.piece?.key ?? 0
    else
      el.innerHTML = `<span class="big-glyph">🎹</span>`

  flashPrompt := (color: string) ->
    promptEl()?.animate [
      { backgroundColor: color }
      { backgroundColor: "transparent" }
    ], { duration: 350, easing: "ease-out" }

  stopTimer := ->
    clearInterval timerId if timerId?
    timerId = undefined

  // Record whatever happened so far: partial sessions still count
  commitSession := ->
    return unless session
    stopTimer()
    recordResults deck, session.results(), Date.now()
    addPracticeSeconds deck, Date.now(), elapsed()
    save()
    tick.increment()

  finishSession := ->
    commitSession()
    screen "summary"

  handleNote := (n: number) ->
    return unless screen() is "session" and session
    e := session!.noteOn n
    tick.increment()
    switch e.status
      "wrong"
        answerText `That was wrong — play: ${e.answer}`
        flashPrompt "rgba(255, 23, 68, 0.35)"
      "correct"
        answerText ""
        flashPrompt "rgba(0, 230, 118, 0.35)"
        if e.done
          finishSession()
        else
          renderPrompt()
      else
        return

  addMidiEventListener (event: MIDIMessageEvent) ->
    {data} := event
    return unless data
    return unless (data[0] & 0xf0) is 0x90 and data[2] > 0
    handleNote data[1]

  // Also reachable programmatically (on-screen keyboards, tests)
  player.lessonNoteOn = handleNote

  // Every way out of the modal — the X button, Escape, a backdrop click, or
  // another modal replacing this one — lands here; without it a dismissed
  // session would stay live and keep judging every MIDI note into an
  // invisible card, persisting bogus ratings
  teardown := ->
    commitSession() if screen() is "session"
    session = undefined

  player.showMyPractice = ->
    tick.increment()
    screen "home"
    Modal.show self.element, closeHandler: teardown

  self :=
    close: -> Modal.hide()

    homeHidden: hiddenUnless "home"
    sessionHidden: hiddenUnless "session"
    summaryHidden: hiddenUnless "summary"

    goalStyle: ->
      tick()
      total := todaySeconds(deck, Date.now()) + (if screen() is "session" then elapsed() else 0)
      `width: ${min 100, round 100 * total / DAILY_GOAL_SECONDS}%`

    goalText: ->
      tick()
      total := todaySeconds(deck, Date.now()) + (if screen() is "session" then elapsed() else 0)
      goal := round DAILY_GOAL_SECONDS / 60
      done := if total >= DAILY_GOAL_SECONDS then " ✓" else ""
      `${floor total / 60}:${String(total % 60).padStart 2, "0"} of ${goal} min today${done}`

    queueText: ->
      tick()
      {due, fresh} := deckCounts deck, pool(), Date.now()
      if due + fresh is 0
        "All caught up — nothing due right now."
      else
        `${due} to review, ${fresh} new`

    pieceText: ->
      tick()
      if deck.piece
        `Working on: ${deck.piece.title or "(untitled song)"}`
      else
        "No piece chosen — cards use general drills. Pick a piece to bias cards toward its key, scale, and notes."

    usePiece: ->
      deck.piece = pieceBiasFromSong player.song
      save()
      tick.increment()

    clearPiece: ->
      deck.piece = undefined
      save()
      tick.increment()

    clearPieceHidden: ->
      tick()
      "hidden" unless deck.piece

    sectionsHidden: ->
      tick()
      "hidden" unless songSections().length

    // The generator's picks for today: weakest sections first, new ones
    // slow with wait-for-note, one due review at tempo
    planItems: ->
      tick()
      lessonPlan(songSections(), pieceProgress(), Date.now()).map (item) ->
        wait := if item.wait then " ⏳" else ""
        SectionRowTemplate
          label: `${item.name} — ${round item.rate * 100}%${wait} (${item.reason})`
          title: "Drill this section at the plan's speed"
          click: -> startDrill item.section, item.rate, item.wait

    sectionItems: ->
      tick()
      progress := pieceProgress()
      songSections().map (s, i) ->
        p := progress[i]
        status := if not p or p.passes is 0 then "not started"
        else if p.mastered then "✓ mastered"
        else `at ${round targetRate(p) * 100}% ・ ${round p.accuracy * 100}% acc`
        SectionRowTemplate
          label: `${s.name or `Section ${i + 1}`} (mm ${s.startMeasure}–${s.endMeasure}) — ${status}`
          title: "Practice this section with the A/B loop at your working speed"
          click: ->
            startDrill i, (if p then targetRate p else 0.6), (not p or p.passes is 0)

    start: ->
      cards := sessionCards deck, pool(), Date.now()
      return unless cards.length
      session = LessonSession cards
      answerText ""
      elapsed 0
      sessionStart = Date.now()
      stopTimer()
      timerId = setInterval ->
        elapsed floor (Date.now() - sessionStart) / 1000
      , 1000
      screen "session"
      tick.increment()
      // the session pane must exist before the prompt draws into it
      setTimeout renderPrompt

    endSession: ->
      finishSession()

    backHome: ->
      session = undefined
      screen "home"
      tick.increment()

    promptLabel: ->
      tick()
      session?.current()?.label ?? ""

    answerText: answerText

    progressText: ->
      tick()
      return "" unless session
      m := floor elapsed() / 60
      s := String(elapsed() % 60).padStart 2, "0"
      `Card ${min session!.index() + 1, session!.total()} of ${session!.total()} ・ ✓ ${session!.correctCount()} ✗ ${session!.incorrectCount()} ・ ${m}:${s}`

    summaryText: ->
      tick()
      return "" unless session
      `${session!.correctCount()} clean, ${session!.incorrectCount()} missed — they'll come back sooner. ${floor elapsed() / 60}:${String(elapsed() % 60).padStart 2, "0"} practiced.`

    element: null as! HTMLElement

  self.element = Template self

  return self

export default MyPractice
